import { useRef, type ReactNode } from 'react';
import { DAY_MIN, HEAD_PX, HOUR_PX, MIN_BLOCK_MIN, minToLabel, snap, type GridInterval } from './gridMath.js';
import { useDragState, type Geometry } from './useDragState.js';
import { useI18n } from '../../i18n/index.js';

/**
 * A Google-Calendar-style week grid (doc 10 §4, §13). Generic over columns so
 * the same component serves the weekly template, the dated overrides tab, and
 * (post-MVP) the appointments calendar. Controlled: it renders `blocks` and, on
 * a completed gesture, calls `onCommit` with the next block list — the parent
 * normalizes each column (merge-on-commit) and feeds fresh blocks back.
 */

export interface TimeGridColumn {
  id: string;
  label: ReactNode;
  /** Secondary label under the header (override dot, "Closed"…). */
  badge?: ReactNode;
  /** Past dates / business-closed days: rendered but not editable. */
  readOnly?: boolean;
  /** Accent this column (e.g. the current day on the dashboard calendar). */
  highlight?: boolean;
  /** Dim this column as a past date (dashboard calendar). */
  past?: boolean;
  /** Tint this column as a Saturday/Sunday (dashboard calendar). */
  weekend?: boolean;
  /** No working hours at all that day — a day off (dashboard calendar). */
  closed?: boolean;
  /** Minute of the day to draw the current-time line at (today's column). */
  nowMin?: number;
}

export interface TimeGridBlock extends GridInterval {
  id: string;
  columnId: string;
  /** `open` = working-hours highlight (background); `busy` = a booked slot. */
  kind: 'editable' | 'ghost' | 'readonly' | 'open' | 'busy';
  label?: string;
  /** Second line under the label — "customer · staff" on a booked slot. Only
   *  rendered when the block is tall enough to show it. */
  subLabel?: string;
  /** A marker at the block's right edge — the speech bubble on a booked slot
   *  whose caller left a message (doc 43). Read-only blocks only. */
  aside?: ReactNode;
  /** Hover tooltip (non-editable blocks). */
  title?: string;
  /** Side-by-side placement for overlapping blocks (see {@link assignLanes}). */
  lane?: number;
  laneCount?: number;
}

export interface TimeGridProps {
  columns: TimeGridColumn[];
  blocks: TimeGridBlock[];
  onCommit(next: TimeGridBlock[]): void;
  onSelect?(id: string | null): void;
  onKeyCommand?(id: string, cmd: 'delete' | 'open'): void;
  /** Click/Enter on a non-editable block (e.g. a booked slot). */
  onActivate?(id: string): void;
  selectedId?: string | null;
  readOnly?: boolean;
  window: { fromMin: number; toMin: number };
  /** Pixels per hour; defaults to the fixed {@link HOUR_PX}. Pass a measured
   * value to stretch the grid to a container's height (dashboard calendar). */
  hourPx?: number;
  /** Column-header height. The hour axis is offset by exactly this, so a taller
   * header (the dashboard's two-line weekday + date) must say so here. */
  headPx?: number;
}

export function TimeGrid({
  columns,
  blocks,
  onCommit,
  onSelect,
  onKeyCommand,
  onActivate,
  selectedId = null,
  readOnly = false,
  window: win,
  hourPx = HOUR_PX,
  headPx = HEAD_PX,
}: TimeGridProps) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLDivElement>(null);
  const colsRef = useRef<HTMLDivElement>(null);
  const spanMin = win.toMin - win.fromMin;
  const pxPerMin = hourPx / 60;

  const geometry: Geometry = {
    minFromClientY(clientY) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return win.fromMin;
      const min = win.fromMin + (clientY - rect.top) / pxPerMin;
      return Math.max(win.fromMin, Math.min(win.toMin, snap(min)));
    },
    columnIdFromClientX(clientX) {
      const rect = colsRef.current?.getBoundingClientRect();
      if (!rect || columns.length === 0) return null;
      const i = Math.floor(((clientX - rect.left) / rect.width) * columns.length);
      return columns[Math.max(0, Math.min(columns.length - 1, i))]?.id ?? null;
    },
  };

  const drag = useDragState({ blocks, columns, geometry, onCommit, onSelect, disabled: readOnly });

  /** Keyboard nudge (doc 10 §8): move/resize the selected block by 15 min or
   * shift it across days. `resizeEnd` moves only the end; `dCol` moves columns. */
  const nudge = (block: TimeGridBlock, dMin: number, resizeEnd: boolean, dCol: number) => {
    let next: TimeGridBlock;
    if (dCol !== 0) {
      const idx = columns.findIndex((c) => c.id === block.columnId);
      const target = columns[Math.max(0, Math.min(columns.length - 1, idx + dCol))];
      if (!target || target.readOnly) return;
      next = { ...block, columnId: target.id };
    } else if (resizeEnd) {
      next = { ...block, endMin: Math.max(block.startMin + MIN_BLOCK_MIN, Math.min(DAY_MIN, block.endMin + dMin)) };
    } else {
      const dur = block.endMin - block.startMin;
      const start = Math.max(0, Math.min(DAY_MIN - dur, block.startMin + dMin));
      next = { ...block, startMin: start, endMin: start + dur };
    }
    onCommit(blocks.map((b) => (b.id === block.id ? next : b)));
  };

  const y = (min: number) => (min - win.fromMin) * pxPerMin;
  const hours: number[] = [];
  for (let m = Math.ceil(win.fromMin / 60) * 60; m <= win.toMin; m += 60) hours.push(m);

  const rendered: TimeGridBlock[] = drag.preview
    ? [...blocks.filter((b) => b.id !== drag.hiddenId), drag.preview]
    : blocks;

  return (
    <div className="timegrid" role="application" aria-label={t('grid.scheduleEditor')}>
      <div className="timegrid-axis" style={{ paddingTop: headPx }}>
        {hours.map((m) => (
          <div key={m} className="timegrid-axis-label" style={{ top: headPx + y(m) }}>
            {minToLabel(m)}
          </div>
        ))}
      </div>

      <div className="timegrid-cols" ref={colsRef}>
        {columns.map((col) => (
          <div
            key={col.id}
            className={
              `timegrid-col${col.highlight ? ' is-highlight' : ''}` +
              `${col.past ? ' is-past' : ''}${col.weekend ? ' is-weekend' : ''}${col.closed ? ' is-closed' : ''}`
            }
          >
            <div className="timegrid-col-head" style={{ height: headPx }}>
              <span className="timegrid-col-label">{col.label}</span>
              {col.badge ? <span className="timegrid-col-badge">{col.badge}</span> : null}
            </div>
            <div
              className={`timegrid-canvas${col.readOnly || readOnly ? ' is-readonly' : ''}`}
              style={{ height: spanMin * pxPerMin }}
              ref={col.id === columns[0]?.id ? canvasRef : undefined}
              onPointerDown={(e) => {
                if (col.readOnly || readOnly) return;
                if (e.target === e.currentTarget) drag.onCanvasPointerDown(e, col.id);
              }}
            >
              {hours.map((m) => (
                <div key={m} className="timegrid-hourline" style={{ top: y(m) }} />
              ))}
              {col.nowMin !== undefined && col.nowMin >= win.fromMin && col.nowMin <= win.toMin ? (
                <div className="timegrid-now" style={{ top: y(col.nowMin) }} aria-hidden />
              ) : null}
              {rendered
                .filter((b) => b.columnId === col.id)
                .map((b) => (
                  <BlockView
                    key={b.id}
                    block={b}
                    top={y(b.startMin)}
                    height={Math.max(12, (b.endMin - b.startMin) * pxPerMin)}
                    selected={b.id === selectedId}
                    editable={b.kind === 'editable' && !col.readOnly && !readOnly}
                    onPointerDown={drag.onBlockPointerDown}
                    onSelect={onSelect}
                    onKeyCommand={onKeyCommand}
                    onActivate={onActivate}
                    onNudge={nudge}
                  />
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EDGE_PX = 8;

function BlockView({
  block,
  top,
  height,
  selected,
  editable,
  onPointerDown,
  onSelect,
  onKeyCommand,
  onActivate,
  onNudge,
}: {
  block: TimeGridBlock;
  top: number;
  height: number;
  selected: boolean;
  editable: boolean;
  onPointerDown(e: React.PointerEvent, block: TimeGridBlock, edge: 'start' | 'end' | null): void;
  onSelect?(id: string | null): void;
  onKeyCommand?(id: string, cmd: 'delete' | 'open'): void;
  onActivate?(id: string): void;
  onNudge(block: TimeGridBlock, dMin: number, resizeEnd: boolean, dCol: number): void;
}) {
  const label = block.label ?? `${minToLabel(block.startMin)} – ${minToLabel(block.endMin)}`;
  const compact = height < 30;
  const cls =
    `timegrid-block timegrid-block-${block.kind}` +
    (selected ? ' is-selected' : '') +
    (compact ? ' is-compact' : '') +
    (block.aside && !editable ? ' has-aside' : '') +
    (block.laneCount && block.laneCount > 1 ? ' is-laned' : '');
  // A one-line block has no room for the second line; the tooltip still has it.
  const body = (
    <>
      <span className="timegrid-block-label">
        <span className="timegrid-block-title">{label}</span>
        {block.subLabel && !compact && <span className="timegrid-block-sub">{block.subLabel}</span>}
      </span>
      {block.aside && !editable && <span className="timegrid-block-aside">{block.aside}</span>}
    </>
  );
  const lanes =
    block.laneCount && block.laneCount > 1
      ? {
          left: `calc(${((block.lane ?? 0) / block.laneCount) * 100}% + 2px)`,
          right: 'auto',
          width: `calc(${100 / block.laneCount}% - 4px)`,
        }
      : undefined;

  if (!editable) {
    const decorative = block.kind === 'ghost' || block.kind === 'open';
    if (!decorative && onActivate) {
      return (
        <button
          type="button"
          className={`${cls} is-clickable`}
          style={{ top, height, ...lanes }}
          title={block.title}
          onClick={() => onActivate(block.id)}
        >
          {body}
        </button>
      );
    }
    return (
      <div className={cls} style={{ top, height, ...lanes }} aria-hidden={decorative} title={block.title}>
        {!decorative && body}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={cls}
      style={{ top, height }}
      aria-label={label}
      onPointerDown={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const offset = e.clientY - rect.top;
        const edge = offset <= EDGE_PX ? 'start' : offset >= rect.height - EDGE_PX ? 'end' : null;
        onPointerDown(e, block, edge);
      }}
      onClick={() => onSelect?.(block.id)}
      onKeyDown={(e) => {
        const step = 15;
        switch (e.key) {
          case 'Delete':
          case 'Backspace':
            e.preventDefault();
            onKeyCommand?.(block.id, 'delete');
            break;
          case 'Enter':
            e.preventDefault();
            onKeyCommand?.(block.id, 'open');
            break;
          case 'ArrowUp':
            e.preventDefault();
            onNudge(block, e.shiftKey ? -step : -step, e.shiftKey, 0);
            break;
          case 'ArrowDown':
            e.preventDefault();
            onNudge(block, step, e.shiftKey, 0);
            break;
          case 'ArrowLeft':
            e.preventDefault();
            onNudge(block, 0, false, -1);
            break;
          case 'ArrowRight':
            e.preventDefault();
            onNudge(block, 0, false, 1);
            break;
        }
      }}
    >
      <span className="timegrid-block-handle timegrid-block-handle-top" aria-hidden />
      {body}
      <span className="timegrid-block-handle timegrid-block-handle-bottom" aria-hidden />
    </button>
  );
}

export { DAY_MIN };
